import React from "react";
import ReactDOM from "react-dom/client";
import InlineDocumentViewer from "./InlineDocumentViewer";
import MediaResource from "./MediaResource";
import Nav from "../../components/layouts/nav";
import { API_URL, DEFAULT_COUNTRY_ID, URLS } from "../../_config";
import { showDate } from "../../_common/functions";
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Footer from "../../components/layouts/footer";
import MetaSection from "../../components/MetaSection";
import { useSelectedCountry, useCountryReady } from "../../_common/hooks/authHook";
import RightBlogList from "../blogs_and_news/RightBlogList";
import BottomBlogList from "../blogs_and_news/BottomBlogList";
import RightNewsList from "../blogs_and_news/RightNewsList";
import BottomNewsList from "../blogs_and_news/BottomNewsList";
import { IMAGE_FETCH_BACKEND_URL } from "../../_config/site_urls";
import { applyButtonGroupStyles } from "../../utils/applyButtonGroupStyles";
import { buildArticleStructuredData } from "../../utils/structuredData";
import Loader from "../../components/loader";
import ContentUnavailable from "../../components/ContentUnavailable";
import { scopeCustomCss } from "../../utils/scopeCustomCss";

const MenuPageDetails = () => {
    const { pageSlug } = useParams<{ pageSlug: string }>();
    const selectedCountry: any = useSelectedCountry();
    // Seed from the SSR-embedded initial data if present (see
    // SsrController::renderPageDetails()) so this doesn't briefly show <Loader/>
    // after createRoot() mounts. ssrSeeded tracks whether *this* mount was seeded,
    // so the pageSlug-change effect below can skip its usual reset-to-null only on
    // that first run, not on later client-side navigation to a different slug.
    const ssrSeeded = React.useRef<boolean>(!!(window as any).__SSR_PROPS__?.page_details);
    const [detailsData, setDetailsData] = React.useState<any>(() => (window as any).__SSR_PROPS__?.page_details ?? null);
    const [notFound, setNotFound] = React.useState<boolean>(false);
    const countryReady: boolean = useCountryReady();
    const [blogList, setBlogList] = React.useState<any>(null);
    const [newsList, setNewsList] = React.useState<any>(null);
    const [template, setTemplate] = React.useState<any>(1);
    // Seeded from window.__SSR_PROPS__ so the raw [[shortcode]] placeholder text
    // isn't briefly visible on a fresh SSR-served/refreshed/direct-URL load —
    // mirrors how detailsData above is already seeded. The client-side fetch
    // still overwrites this once it resolves, same as before.
    const [resourceList, setResourceList] = React.useState<any>(() => (window as any).__SSR_PROPS__?.resourcess ?? null);
    const [processedHtml, setProcessedHtml] = React.useState<string>("");
    const [hasFaqAccordion, setHasFaqAccordion] = React.useState<boolean>(false);
    const contentRef = React.useRef<HTMLDivElement>(null);
    const pageRequestIdRef = React.useRef<number>(0);

    const navigate = useNavigate();
    const location = useLocation();

    const getPageDetails = async (_slug: string) => {
        const requestId = ++pageRequestIdRef.current;
        try {
            const response = await fetch(API_URL.MENU_PAGES.PAGE_DETAILS(selectedCountry?.id || DEFAULT_COUNTRY_ID, _slug))
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            const resp = await response.json();
            if (requestId !== pageRequestIdRef.current) return; // superseded by a newer request
            console.log('Response from API:', resp.data);
            const pageDetails = resp.data?.page_details;
            if (pageDetails) {
                setTemplate(pageDetails?.template_id || 1);
                setDetailsData(pageDetails);
                setNotFound(false);
                const blogs = resp.data?.blog?.data || [];
                setBlogList(blogs.slice(0, 4));
                const news = resp.data?.latest_news || [];
                setNewsList(news.slice(0, 4));
                setResourceList(resp.data?.resourcess || [])
            } else {
                setDetailsData(null);
                setBlogList([]);
                setNewsList([]);
                setResourceList([]);
                setNotFound(true);
            }
        } catch (error) {
            if (requestId !== pageRequestIdRef.current) return; // stale — a newer request already superseded this one
            console.log('Error occurred while making the API request.');
            // Handle error
            setDetailsData(null)
            setBlogList([])
            setNewsList([])
            setNotFound(true)
        }
    }

    const processHtmlForAccordion = (htmlString: any) => {
        if (!htmlString) return { html: "", hasFaq: false };
        if (typeof window === 'undefined') return { html: htmlString, hasFaq: false };

        // Apply resource placeholder replacement and image path fixes first
        htmlString = replaceResourcePlaceholders(htmlString, resourceList || []);

        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlString, "text/html");

        // Check if faq-accordion class exists
        const faqContainers = doc.querySelectorAll("div.faq-accordion, div[class*='faq-accordion']");
        const hasFaq = faqContainers.length > 0;

        // Check if accordion classes exist
        // const accordionContainers = doc.querySelectorAll("div.accordion-one-col, div.accordion-two-col, div[class*='accordion-one-col'], div[class*='accordion-two-col']");
        // const hasAccordion = accordionContainers.length > 0;
        if (hasFaq) {
            // Add style to hide Bootstrap's default accordion icon and ensure custom icon positioning
            const style = doc.createElement("style");
            style.textContent = `
                    .faq-accordian .accordion-button::after {
                        display: none !important;
                    }
                    .faq-accordian .accordion-button {
                        display: flex !important;
                        justify-content: space-between !important;
                        align-items: center !important;
                    }
                `;
            doc.head.appendChild(style);

            // Convert FAQ structure to Bootstrap accordion format
            faqContainers.forEach((container, containerIndex) => {
                // Add accordion wrapper class
                container.classList.add("accordion", "faq-accordian");
                container.setAttribute("id", `blogAccordion${containerIndex}`);

                // Find all FAQ items (including nested in faq-column)
                const faqItems = container.querySelectorAll(".faq-item");
                let itemIndex = 0;

                faqItems.forEach((item: Element) => {
                    const questionContainer = item.querySelector(".faq-question");
                    const answerContainer = item.querySelector(".faq-answer");

                    if (questionContainer && answerContainer) {
                        // Convert to accordion-item structure
                        item.classList.add("accordion-item");

                        // Get question text (from h4, h3, h5, or text content)
                        const questionHeading = questionContainer.querySelector("h2,h4, h3, h6, h5");
                        let questionText = "";

                        if (questionHeading) {
                            questionText = questionHeading.textContent || questionHeading.innerHTML || "";
                        } else {
                            // Clone to remove toggle
                            const questionClone = questionContainer.cloneNode(true) as Element;
                            const toggle = questionClone.querySelector(".faq-toggle");
                            if (toggle) {
                                toggle.remove();
                            }
                            questionText = questionClone.textContent || questionClone.innerHTML || "";
                        }

                        // Remove toggle from original if exists
                        const toggle = questionContainer.querySelector(".faq-toggle");
                        if (toggle) {
                            toggle.remove();
                        }

                        // Create accordion header structure
                        const headerId = `heading${containerIndex}_${itemIndex}`;
                        const collapseId = `collapse${containerIndex}_${itemIndex}`;

                        const accordionHeader = doc.createElement("h2");
                        accordionHeader.className = "accordion-header";
                        accordionHeader.id = headerId;

                        const button = doc.createElement("button");
                        button.className = "accordion-button collapsed";
                        button.type = "button";
                        button.setAttribute("data-bs-toggle", "collapse");
                        button.setAttribute("data-bs-target", `#${collapseId}`);
                        button.setAttribute("aria-expanded", "false");
                        button.setAttribute("aria-controls", collapseId);

                        // Preserve inline background-color from the original .faq-question div
                        const questionEl = questionContainer as HTMLElement;
                        const inlineBg = questionEl.style.backgroundColor;
                        if (inlineBg) {
                            button.style.setProperty('background-color', inlineBg, 'important');
                        }

                        // Add question text on the left
                        const questionSpan = doc.createElement("span");
                        questionSpan.textContent = questionText.trim();
                        questionSpan.style.flex = "1";
                        questionSpan.style.textAlign = "left";
                        questionSpan.style.paddingRight = "10px";
                        button.appendChild(questionSpan);

                        // Add plus/minus icon on the right side
                        const iconSpan = doc.createElement("span");
                        iconSpan.className = "faq-accordion-icon";
                        // iconSpan.textContent = "+";
                        iconSpan.style.marginLeft = "auto";
                        iconSpan.style.fontSize = "1.5em";
                        iconSpan.style.fontWeight = "bold";
                        iconSpan.style.flexShrink = "0";
                        iconSpan.style.lineHeight = "1";
                        iconSpan.style.minWidth = "24px";
                        iconSpan.style.textAlign = "center";
                        iconSpan.style.display = "inline-flex";
                        iconSpan.style.alignItems = "center";
                        iconSpan.style.justifyContent = "center";
                        button.appendChild(iconSpan);


                        accordionHeader.appendChild(button);

                        // Create accordion collapse structure
                        const collapseDiv = doc.createElement("div");
                        collapseDiv.id = collapseId;
                        collapseDiv.className = "accordion-collapse collapse";
                        collapseDiv.setAttribute("aria-labelledby", headerId);
                        collapseDiv.setAttribute("data-bs-parent", `#blogAccordion${containerIndex}`);

                        const accordionBody = doc.createElement("div");
                        accordionBody.className = "accordion-body";
                        accordionBody.innerHTML = answerContainer.innerHTML;

                        collapseDiv.appendChild(accordionBody);

                        // Add event listeners to toggle icon when accordion opens/closes
                        // Use Bootstrap's collapse events for accurate state tracking
                        collapseDiv.addEventListener('show.bs.collapse', () => {
                            iconSpan.textContent = "−";
                            button.setAttribute("aria-expanded", "true");
                            button.classList.remove("collapsed");
                        });

                        collapseDiv.addEventListener('hide.bs.collapse', () => {
                            iconSpan.textContent = "+";
                            button.setAttribute("aria-expanded", "false");
                            button.classList.add("collapsed");
                        });

                        // Also handle click event as fallback for immediate feedback
                        button.addEventListener("click", () => {
                            setTimeout(() => {
                                const isExpanded = button.getAttribute("aria-expanded") === "true";
                                iconSpan.textContent = isExpanded ? "−" : "+";
                            }, 50);
                        });

                        // Replace question and answer with accordion structure
                        questionContainer.replaceWith(accordionHeader);
                        answerContainer.replaceWith(collapseDiv);

                        itemIndex++;
                    }
                });
            });
        }

        // Fix image src paths
        const images = doc.querySelectorAll("img");
        images.forEach((img) => {
            const src = img.getAttribute("src");
            if (src && src.startsWith("..")) {
                const cleanedSrc = src.replace(/^(\.\.\/)+/, "");
                img.setAttribute("src", `${IMAGE_FETCH_BACKEND_URL}/${cleanedSrc}`);
            }
        });

        let finalHtml = doc.body.innerHTML;
        finalHtml = finalHtml.replace(/(<p>\s*);/g, '$1');

        return { html: finalHtml, hasFaq };
    };

    React.useEffect(() => {
        console.log('Page Slug from URL:', pageSlug);
        if (!pageSlug) {
            navigate(URLS.NOT_FOUND + location.search, { replace: true });
            return;
        }
        if (!countryReady) return;
        if (pageSlug) {
            getPageDetails(pageSlug);
            setTemplate(1);
            // Skip the reset only on the run that already has SSR-seeded content —
            // every subsequent slug change (real client-side navigation) still
            // resets to null/Loader exactly as before. resourceList must be
            // guarded the same way detailsData/notFound are — otherwise the
            // SSR-seeded resource list gets wiped back to [] immediately after
            // mount, reintroducing the raw [[shortcode]] placeholder flash this
            // seeding was meant to prevent.
            if (!ssrSeeded.current) {
                setDetailsData(null);
                setNotFound(false);
                setResourceList([]);
            }
            ssrSeeded.current = false;
            setBlogList([]);
            setNewsList([]);
        }
    }, [pageSlug, selectedCountry, countryReady]);

    // Consume-and-clear: the lazy initializer above only ever runs once per mounted
    // instance, but without clearing this, navigating away and later back to a
    // *different* slug (a genuine unmount+remount, not just a param change on an
    // already-mounted instance) would let a future mount pick up this stale value.
    React.useEffect(() => {
        delete (window as any).__SSR_PROPS__;
    }, []);

    React.useEffect(() => {
        if (detailsData?.long_description) {
            const { html, hasFaq } = processHtmlForAccordion(detailsData.long_description);
            setProcessedHtml(html || fixImageSrcPaths(detailsData.long_description) || "");
            setHasFaqAccordion(hasFaq);
        } else {
            setProcessedHtml("");
            setHasFaqAccordion(false);
        }
    }, [detailsData, resourceList]);

    // resource.original is interpolated into raw HTML attribute strings below
    // (this markup is injected via dangerouslySetInnerHTML) — escape it so a
    // stray `"` in the URL can't break out of the attribute.
    const escapeHtmlAttr = (value: string) =>
        value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    const replaceResourcePlaceholders = (htmlString: any, resources: any[]) => {
        if (!htmlString || !resources || resources.length === 0) return htmlString;

        // Match patterns like <p>[[resource_value]]</p>
        const placeholderRegex = /<p>\[\[([^\]]+)\]\]<\/p>/g;

        return htmlString.replace(placeholderRegex, (match: string, resourceValue: string) => {
            // Find the corresponding resource in resourceList
            // Try matching by id, slug, value, or name
            const resource = resources.find((res: any) =>
                res?.short_code?.toString() === resourceValue
            );
            // console.log(resource);
            if (resource?.original) {
                const url: string = resource.original;
                const safeUrl = escapeHtmlAttr(url);
                // Strip query string/hash before checking the extension — a URL
                // like image.jpg?v=2 would otherwise never match .endsWith('.jpg').
                const pathOnly = url.toLowerCase().split('?')[0].split('#')[0];
                const isVideoByType = resource?.access_type === 'Video';
                const isAudioByType = resource?.access_type === 'Audio';

                if (pathOnly.endsWith('.pdf') || pathOnly.endsWith('.doc') || pathOnly.endsWith('.docx')) {
                    const type = pathOnly.endsWith('.pdf') ? 'pdf' : pathOnly.endsWith('.docx') ? 'docx' : 'doc';
                    // InlineDocumentViewer is mounted into this div by useEffect after HTML renders
                    return `<div class="idv-mount" data-url="${safeUrl}" data-type="${type}" style="width:100%;"></div>`;
                }

                const imageExts = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];
                const videoExts = ['.mp4', '.webm', '.ogv', '.mov', '.m4v'];
                const audioExts = ['.mp3', '.wav', '.ogg', '.m4a'];

                // MediaResource is mounted into these by the same useEffect that
                // handles .idv-mount — gives image/video/audio real error/fallback
                // handling instead of a bare, unguarded <img>/<video>/<audio> tag.
                if (imageExts.some((ext) => pathOnly.endsWith(ext))) {
                    return `<div class="resource-mount" data-url="${safeUrl}" data-type="image" style="margin-bottom:15px;"></div>`;
                }

                if (isVideoByType || videoExts.some((ext) => pathOnly.endsWith(ext))) {
                    return `<div class="resource-mount" data-url="${safeUrl}" data-type="video" style="margin-bottom:15px;"></div>`;
                }

                if (isAudioByType || audioExts.some((ext) => pathOnly.endsWith(ext))) {
                    return `<div class="resource-mount" data-url="${safeUrl}" data-type="audio" style="margin-bottom:15px;"></div>`;
                }

                // anything else — generic iframe fallback
                return `<iframe src="${safeUrl}#toolbar=0" width="100%" height="800px"></iframe>`;
            }

            // If no match found, return the original placeholder
            return match;
        });
    };

    const fixImageSrcPaths = (htmlString: any) => {
        if (!htmlString) return "";
        if (typeof window === 'undefined') return htmlString;

        // First replace resource placeholders
        let processedHtml = replaceResourcePlaceholders(htmlString, resourceList || []);

        const parser = new DOMParser();
        // const doc = parser.parseFromString(htmlString, "text/html");
        const doc = parser.parseFromString(processedHtml, "text/html");

        // Fix image src paths
        const images = doc.querySelectorAll("img");
        images.forEach((img) => {
            const src = img.getAttribute("src");
            if (src && src.startsWith("..")) {
                const cleanedSrc = src.replace(/^(\.\.\/)+/, "");
                img.setAttribute("src", `${IMAGE_FETCH_BACKEND_URL}/${cleanedSrc}`);
            }
        });

        let finalHtml = doc.body.innerHTML;

        finalHtml = finalHtml.replace(/(<p>\s*);/g, '$1');

        return finalHtml;
    };

    // Apply Button Group data-attribute styles + initialize Bootstrap accordion after render
    React.useEffect(() => {
        if (contentRef.current) {
            applyButtonGroupStyles(contentRef.current);
        }
        if (hasFaqAccordion && contentRef.current) {
            const accordionElements = contentRef.current.querySelectorAll('[data-bs-toggle="collapse"]');
            accordionElements.forEach((_element) => {
                if (typeof window !== 'undefined' && (window as any).bootstrap) {
                    // Bootstrap is available, accordion should work
                }
            });
        }
    }, [hasFaqAccordion, processedHtml]);

    const cssScopeId = detailsData?.id ? `custom-css-menu-${detailsData.id}` : undefined;
    const scopedCustomCss = React.useMemo(() => {
        if (!cssScopeId || !detailsData?.custom_css) return "";
        return scopeCustomCss(detailsData.custom_css, `#${cssScopeId}`);
    }, [cssScopeId, detailsData?.custom_css]);

    // Mount InlineDocumentViewer (pdf/doc/docx) and MediaResource (image/video/audio)
    // into their placeholder divs — same mount-point pattern for both, since
    // this content comes from dangerouslySetInnerHTML and React components can't
    // be declared inline there.
    React.useEffect(() => {
        if (!contentRef.current) return;

        const docMountPoints = contentRef.current.querySelectorAll<HTMLElement>(".idv-mount");
        const mediaMountPoints = contentRef.current.querySelectorAll<HTMLElement>(".resource-mount");
        if (docMountPoints.length === 0 && mediaMountPoints.length === 0) return;

        const roots: ReturnType<typeof ReactDOM.createRoot>[] = [];

        docMountPoints.forEach((el) => {
            const url = el.getAttribute("data-url") || "";
            const type = el.getAttribute("data-type") as "pdf" | "doc" | "docx" | "";
            if (!url || !type) return;

            const root = ReactDOM.createRoot(el);
            root.render(<InlineDocumentViewer fileUrl={url} fileType={type} />);
            roots.push(root);
        });

        mediaMountPoints.forEach((el) => {
            const url = el.getAttribute("data-url") || "";
            const type = el.getAttribute("data-type") as "image" | "video" | "audio" | "";
            if (!url || !type) return;

            const root = ReactDOM.createRoot(el);
            root.render(<MediaResource fileUrl={url} fileType={type} />);
            roots.push(root);
        });

        return () => {
            roots.forEach((root) => root.unmount());
        };
    }, [processedHtml]);

    if (detailsData === null && !notFound) {
        return <Loader />;
    }
    return (
        <div className='bg-white'>
            {/* <MetaSection
                title={detailsData?.meta_title || "Page Details"}
                desc={detailsData?.meta_description || "Page details page"}
                keywords={detailsData?.meta_keyword || "page, details"}
                url={location.pathname}
                type="article"
                structuredData={buildArticleStructuredData({
                    type: "Article",
                    headline: detailsData?.title,
                    description: detailsData?.meta_description,
                    datePublished: detailsData?.publish_date,
                    url: location.pathname,
                })}
                noindex={notFound}
            /> */}
            <Nav />
            <div className="subscription">
                <div className="container">
                    {/*<div className="banner-text latest_news_title">
                        <h2 data-aos="fade-up" className="text-left pb-1">
                            {detailsData?.title} </h2>
                    </div>*/}
                    <div className="row">
                        <div className={` col-lg-${template == 1 || template == 3 ? '8' : '12'}  col-md-12`}>
                            <div className="news_detailscontent">
                                {notFound ? (
                                    <ContentUnavailable label="Page" />
                                ) : (
                                    <div className="news_detailstxt" ref={contentRef} id={cssScopeId}>
                                        {scopedCustomCss && <style>{scopedCustomCss}</style>}
                                        <div dangerouslySetInnerHTML={{ __html: processedHtml || "" }} />
                                    </div>
                                )}
                            </div>
                        </div>

                        {
                            template === 1 ?
                                <div className="col-lg-4 col-md-12">
                                    <RightBlogList blogList={blogList} title="Blogs" />
                                    <RightNewsList newsList={newsList} title="News" />
                                </div> :
                                template === 2 ?
                                    <>
                                        <BottomBlogList blogList={blogList} title="Blogs" />
                                        <BottomNewsList newsList={newsList} title="News" />
                                    </>
                                    :
                                    template === 4 ?
                                        null
                                        :
                                        <>
                                            <div className="col-lg-4 col-md-12">
                                                <RightNewsList newsList={newsList} title="News" />
                                            </div>
                                            <BottomBlogList blogList={blogList} title="Blogs" />
                                        </>

                        }
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
}
export default MenuPageDetails;